import React, { useState, useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { Formik, Form, FieldArray } from 'formik'
import { Button, Row, Col, Table } from 'react-bootstrap'
import Field from '../_library/Field'
import Select from '../_library/Select'
import DataTable from '../_library/DataTable'
import DateTimePickerReact from '../_library/DateTimePickerReact'
import { formatCurrency } from '../utils/currencyUtils'
import { CREATE_BULK_INVOICE, UPDATE_BULK_INVOICE, SEND_BULK_INVOICE, FETCH_BULK_INVOICE } from '../../_common/redux/bulkInvoices/actions'
import { baseRequest } from '../../_common/core/http_services'
import { requiredValidator, isEmailValidator, minNumberValidator, emptyArrayValidator } from '../../_common/core/validation'
import moment from 'moment'

const BulkInvoiceForm = ({ event, invoice, currentInvoice, onSuccess, onCancel, createInvoice, updateInvoice, sendInvoice, fetchBulkInvoice, match, history }) => {
  const [availableItems, setAvailableItems] = useState({ ticket_types: [], addons: [], tax_info: { tax_name: null, tax_rate: 0, tax_class_id: null } })
  const [isLoading, setIsLoading] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [apiError, setApiError] = useState(null)
  const [isLoadingItems, setIsLoadingItems] = useState(true)
  const formikRef = useRef(null)
  const addItemRef = useRef(null)
  

  useEffect(() => {
    let isMounted = true
    const eventId = event?.id || match?.params?.id
    const invoiceId = invoice?.id || match?.params?.invoiceId
    
    if (eventId) {
      // If we have an invoice ID (either from props or URL), fetch full invoice details using Redux action
      if (invoiceId) {
        fetchBulkInvoice(eventId, invoiceId).then(() => {
          // After fetching invoice, fetch available items
          if (isMounted) {
            fetchAvailableItems(eventId, isMounted)
          }
        }).catch(error => {
          if (isMounted) {
            setApiError('Unable to load invoice details.')
            fetchAvailableItems(eventId, isMounted) // Fallback to just loading available items
          }
        })
      } else {
        fetchAvailableItems(eventId, isMounted)
      }
    } else {
      // If no event, stop loading and show error
      if (isMounted) {
        setIsLoadingItems(false)
        setApiError('Event information is not available')
      }
    }

    return () => {
      isMounted = false
    }
  }, [event?.id, match?.params?.eventId, match?.params?.event_id, match?.params?.invoiceId, invoice?.id])

  // Auto-populate tax information for new invoices when API data loads
  useEffect(() => {
    if (!invoice && availableItems.tax_info && !isLoadingItems && formikRef.current) {
      // Only auto-populate if user hasn't changed tax fields and we have tax info from event
      const currentValues = formikRef.current.values
      if (availableItems.tax_info.tax_name && currentValues.tax_name === 'Tax') {
        formikRef.current.setFieldValue('tax_name', availableItems.tax_info.tax_name)
      }
      if (availableItems.tax_info.tax_rate > 0 && currentValues.tax_rate === 0) {
        formikRef.current.setFieldValue('tax_rate', availableItems.tax_info.tax_rate)
      }
    }
  }, [availableItems.tax_info, isLoadingItems, invoice])



  const fetchAvailableItems = async (eventId, isMounted = true) => {
    const useEventId = eventId || event?.id
    
    if (!useEventId) {
      if (isMounted) {
        setIsLoadingItems(false)
        setApiError('Event information is not available')
      }
      return
    }
    
    try {
      if (isMounted) {
        setIsLoadingItems(true)
        setApiError(null)
      }
      const response = await baseRequest.get(`/event/${useEventId}/bulk-invoices/available-items`)
      // Extract data from attributes and set it in the expected structure
      const apiData = response.data.data?.attributes || {}
      if (isMounted) {
        setAvailableItems({
          ticket_types: apiData.ticket_types || [],
          addons: apiData.addons || [],
          tax_info: apiData.tax_info || { tax_name: null, tax_rate: 0, tax_class_id: null }
        })
      }
    } catch (error) {
      console.error('Failed to fetch available items:', error)
      if (isMounted) {
        setApiError('Unable to load available items. Please try again later.')
        // Keep empty arrays so the form still works
        setAvailableItems({ 
          ticket_types: [], 
          addons: [],
          tax_info: { tax_name: null, tax_rate: 0, tax_class_id: null }
        })
      }
    } finally {
      if (isMounted) {
        setIsLoadingItems(false)
      }
    }
  }

  // Use currentInvoice from Redux if available (when editing), otherwise fall back to invoice prop
  const invoiceData = currentInvoice || invoice
  
  const getInitialValues = (invoiceData) => {
    // Extract tax info from tax_class if available
    let taxName = 'Tax'
    let taxRate = 0
    
    if (invoiceData.tax_class && invoiceData.tax_class.rates && invoiceData.tax_class.rates.length > 0) {
      const firstRate = invoiceData.tax_class.rates[0]
      taxName = firstRate.tax_name || 'Tax'
      taxRate = parseFloat(firstRate.rate) || 0
    }
    
    return {
      customer_email: invoiceData.customer_email,
      customer_name: invoiceData.customer_name,
      customer_company: invoiceData.customer_company || '',
      description: invoiceData.description || '',
      notes: invoiceData.notes || '',
      due_date: invoiceData.due_date ? moment(invoiceData.due_date).toDate() : null,
      tax_rate: taxRate,
      tax_name: taxName,
      items: (invoiceData.items || []).map(item => {
        // Handle different possible field structures
        let itemType = ''
        let itemId = ''
        
        // Handle the new backend structure: item_type, item_id, item_name
        if (item.item_type && item.item_id) {
          // New backend structure - item_type is already 'ticket' or 'addon'
          itemType = item.item_type
          itemId = item.item_id
        } else if (item.ticket_type_id && item.ticket_type_id !== null && item.ticket_type_id !== 'null') {
          // Legacy structure - ticket
          itemType = 'ticket'
          itemId = item.ticket_type_id
        } else if (item.addon_id && item.addon_id !== null && item.addon_id !== 'null') {
          // Legacy structure - addon
          itemType = 'addon'
          itemId = item.addon_id
        } else if (item.type) {
          // Fallback structure
          itemType = item.type === 'ticket_type' ? 'ticket' : (item.type === 'addon' ? 'addon' : item.type)
          itemId = item.item_id || item.id
        } else {
          console.error('Backend error: Missing item type information for item:', item)
        }
        
        const mappedItem = {
          type: itemType,
          id: String(itemId), // Ensure it's a string for the select
          quantity: parseInt(item.quantity) || 1,
          custom_price: parseFloat(item.custom_price || item.unit_price || item.price || 0)
        }
        
        return mappedItem
      })
    }
  }

  const initialValues = invoiceData ? getInitialValues(invoiceData) : {
    customer_email: '',
    customer_name: '',
    customer_company: '',
    description: '',
    notes: '',
    due_date: null,
    tax_rate: availableItems.tax_info?.tax_rate || 0,
    tax_name: availableItems.tax_info?.tax_name || 'Tax',
    tax_class_id: availableItems.tax_info?.tax_class_id || null,
    items: []
  }

  const handleSubmit = async (values, { setSubmitting, setErrors }) => {
    setIsLoading(true)
    try {
      // Extract event ID the same way we do for API calls
      const eventId = event?.id || match?.params?.id || match?.params?.eventId || match?.params?.event_id
      
      if (!eventId) {
        throw new Error('Event ID not found')
      }
      
      // Calculate totals for invoice
      const calculatedItems = values.items.map(item => {
        const defaultPrice = getItemPrice(item.type, item.id)
        const finalPrice = item.custom_price ? parseFloat(item.custom_price) : defaultPrice
        const quantity = parseInt(item.quantity)
        const totalPrice = finalPrice * quantity
        
        return {
          type: item.type,
          id: parseInt(item.id),
          name: getItemName(item.type, item.id),
          quantity: quantity,
          unit_price: finalPrice,
          total_price: totalPrice,
          custom_price: finalPrice,
          original_price: item.original_price || defaultPrice,
          price_override_reason: item.custom_price ? '' : null
        }
      })
      
      // Calculate invoice totals
      const subtotal = calculatedItems.reduce((sum, item) => sum + item.total_price, 0)
      const taxAmount = calculateTaxAmount(subtotal, values.tax_rate)
      const total = subtotal + taxAmount

      const data = {
        ...values,
        due_date: values.due_date ? moment(values.due_date).format('YYYY-MM-DD HH:mm:ss') : null,
        items: calculatedItems,
        subtotal_amount: subtotal,
        tax_amount: taxAmount,
        tax_rate: parseFloat(values.tax_rate) || 0,
        tax_name: values.tax_name || 'Tax',
        tax_class_id: values.tax_class_id || null,
        total_amount: total,
        currency: event?.currency?.currency || 'USD'
      }

      let result
      if (invoice) {
        result = await updateInvoice(eventId, invoice.id, data)
        onSuccess(invoice.id)
      } else {
        result = await createInvoice(eventId, data)
        const invoiceId = result?.id || result?.attributes?.id
        onSuccess(invoiceId)
      }
    } catch (error) {
      console.error('Failed to save invoice:', error)
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors)
      }
    } finally {
      setIsLoading(false)
      setSubmitting(false)
    }
  }

  const handleSendInvoice = async () => {
    if (!invoice || invoice.status !== 'draft') return
    
    setIsSending(true)
    try {
      // Use the same fallback logic as other functions in this component
      const eventId = event?.id || match?.params?.id || match?.params?.eventId || match?.params?.event_id
      
      if (!eventId) {
        throw new Error('Event ID not found')
      }
      
      const result = await sendInvoice(eventId, invoice.id)
      onSuccess(invoice.id)
    } catch (error) {
      console.error('Failed to send invoice:', error)
    } finally {
      setIsSending(false)
    }
  }

  const handleSaveAndSendInvoice = async (values, { setSubmitting, setErrors }) => {
    if (!invoice || invoice.status !== 'draft') return
    
    setIsSending(true)
    try {
      // Use the same fallback logic as other functions in this component
      const eventId = event?.id || match?.params?.id || match?.params?.eventId || match?.params?.event_id
      
      if (!eventId) {
        throw new Error('Event ID not found')
      }
      
      // Calculate items data the same way as handleSubmit
      const calculatedItems = values.items.map(item => {
        const defaultPrice = getItemPrice(item.type, item.id)
        const finalPrice = item.custom_price ? parseFloat(item.custom_price) : defaultPrice
        const quantity = parseInt(item.quantity)
        const totalPrice = finalPrice * quantity
        
        return {
          type: item.type,
          id: parseInt(item.id),
          name: getItemName(item.type, item.id),
          quantity: quantity,
          unit_price: finalPrice,
          total_price: totalPrice,
          custom_price: finalPrice,
          original_price: item.original_price || defaultPrice,
          price_override_reason: item.custom_price ? '' : null
        }
      })
      
      // Calculate invoice totals
      const subtotal = calculatedItems.reduce((sum, item) => sum + item.total_price, 0)
      const taxAmount = calculateTaxAmount(subtotal, values.tax_rate)
      const total = subtotal + taxAmount

      const data = {
        ...values,
        due_date: values.due_date ? moment(values.due_date).format('YYYY-MM-DD HH:mm:ss') : null,
        items: calculatedItems,
        subtotal_amount: subtotal,
        tax_amount: taxAmount,
        tax_rate: parseFloat(values.tax_rate) || 0,
        tax_name: values.tax_name || 'Tax',
        tax_class_id: values.tax_class_id || null,
        total_amount: total,
        currency: event?.currency?.currency || 'USD'
      }

      // First save the invoice
      await updateInvoice(eventId, invoice.id, data)
      
      // Then send the invoice
      await sendInvoice(eventId, invoice.id)
      onSuccess(invoice.id)
    } catch (error) {
      console.error('Failed to save and send invoice:', error)
      if (error.response?.data?.errors && setErrors) {
        setErrors(error.response.data.errors)
      }
    } finally {
      setIsSending(false)
      if (setSubmitting) {
        setSubmitting(false)
      }
    }
  }

  // Validate items array
  const validateItems = items => {
    if (!items || items.length === 0) {
      return 'At least one item is required'
    }
    
    for (let i = 0; i < items.length; i++) {
      const item = items[i]
      if (!item.type) return `Item ${i + 1}: Type is required`
      if (!item.id) return `Item ${i + 1}: Item selection is required`
      if (!item.quantity || item.quantity < 1) return `Item ${i + 1}: Quantity must be at least 1`
      if (item.custom_price !== null && item.custom_price !== undefined && item.custom_price < 0) {
        return `Item ${i + 1}: Price must be positive`
      }
    }
    
    return null
  }

  // Check if form has all required fields filled
  const isFormValid = (values, errors) => {
    // Check basic required fields
    if (!values.customer_email || !values.customer_name) {
      return false
    }
    
    // Check for critical validation errors (not field-level incomplete errors)
    if (errors.customer_email || errors.customer_name) {
      return false
    }
    
    // Check items array
    if (!values.items || values.items.length === 0) {
      return false
    }
    
    // Check each item has required fields and is valid
    for (let i = 0; i < values.items.length; i++) {
      const item = values.items[i]
      const itemErrors = errors.items && errors.items[i]
      
      // Item must have required fields
      if (!item.type || !item.id || !item.quantity || item.quantity < 1) {
        return false
      }
      
      // Check for item-specific errors (but ignore empty field errors for optional fields)
      if (itemErrors && (itemErrors.type || itemErrors.id || itemErrors.quantity)) {
        return false
      }
    }
    
    return true
  }

  const calculateSubtotal = items => {
    if (!items || !Array.isArray(items)) return 0
    return items.reduce((total, item) => {
      const price = parseFloat(item.custom_price || getItemPrice(item.type, item.id) || 0)
      const quantity = parseInt(item.quantity || 0)
      return total + (price * quantity)
    }, 0)
  }

  const calculateTaxAmount = (subtotal, taxRate) => {
    const rate = parseFloat(taxRate)
    if (!rate || rate === 0 || subtotal === 0) return 0
    return (subtotal * rate) / 100
  }

  const calculateTotal = (items, taxRate) => {
    const subtotal = calculateSubtotal(items)
    const taxAmount = calculateTaxAmount(subtotal, taxRate)
    return subtotal + taxAmount
  }

  const getItemPrice = (type, id) => {
    if (!type || !id || !availableItems) return 0
    const items = type === 'ticket' ? (availableItems.ticket_types || []) : (availableItems.addons || [])
    const item = items.find(i => i.id == id)
    return item ? parseFloat(item.price || 0) : 0
  }

  const getItemName = (type, id) => {
    if (!type || !id || !availableItems) return ''
    const items = type === 'ticket' ? (availableItems.ticket_types || []) : (availableItems.addons || [])
    const item = items.find(i => i.id == id)
    return item ? (item.name || '') : ''
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validate={values => {
        const errors = {}
        const itemsError = validateItems(values.items)
        if (itemsError) {
          errors.items = itemsError
        }
        return errors
      }}
      enableReinitialize
    >
      {(formikProps) => {
        const { values, errors, touched, setFieldValue, isSubmitting, setSubmitting, setErrors } = formikProps
        
        // Store formik instance for use in component-level useEffect
        formikRef.current = formikProps
        
        return (
        <Form className="bulk-invoice-form">
          {apiError && (
            <div className="alert alert-warning mb-3">
              <i className="fa fa-exclamation-triangle"></i> {apiError}
            </div>
          )}
          <Row>
            <Col md={6}>
              <Field
                name="customer_email"
                label="Customer Email"
                type="email"
                value={values.customer_email}
                onChange={e => setFieldValue('customer_email', e.target.value)}
                validate={value => {
                  const requiredError = requiredValidator('Customer email is required')(value)
                  if (requiredError) return requiredError
                  return isEmailValidator('Invalid email')(value)
                }}
                error={errors.customer_email}
                touched={touched.customer_email}
                disabled={invoice && invoice.status !== 'draft'}
              />
            </Col>
            <Col md={6}>
              <Field
                name="customer_name"
                label="Customer Name"
                value={values.customer_name}
                onChange={e => setFieldValue('customer_name', e.target.value)}
                validate={requiredValidator('Customer name is required')}
                error={errors.customer_name}
                touched={touched.customer_name}
                disabled={invoice && invoice.status !== 'draft'}
              />
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Field
                name="customer_company"
                label="Company (Optional)"
                value={values.customer_company}
                onChange={e => setFieldValue('customer_company', e.target.value)}
                error={errors.customer_company}
                touched={touched.customer_company}
                disabled={invoice && invoice.status !== 'draft'}
              />
            </Col>
            <Col md={6}>
              <DateTimePickerReact
                name="due_date"
                label="Due Date (Optional)"
                value={values.due_date}
                onChange={date => setFieldValue('due_date', date)}
                minDate={new Date()}
                disabled={invoice && invoice.status !== 'draft'}
              />
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <Field
                name="description"
                label="Description (Optional)"
                component="textarea"
                rows={2}
                value={values.description}
                onChange={e => setFieldValue('description', e.target.value)}
                error={errors.description}
                touched={touched.description}
                disabled={invoice && invoice.status !== 'draft'}
              />
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <Field
                name="notes"
                label="Internal Notes (Optional)"
                component="textarea"
                rows={2}
                value={values.notes}
                onChange={e => setFieldValue('notes', e.target.value)}
                error={errors.notes}
                touched={touched.notes}
                disabled={invoice && invoice.status !== 'draft'}
              />
            </Col>
          </Row>


          <div className="d-flex justify-content-between align-items-center mt-4 mb-3">
            <h5 className="mb-0" style={{color: '#ffffff'}}>Invoice Items</h5>
            <div className="btn-create-brand" style={{marginLeft: 'auto'}}>
              <Button
                className="btn btn-primary"
                type="button"
                size="sm"
                onClick={() => addItemRef.current && addItemRef.current({ type: '', id: '', quantity: 1, custom_price: null })}
              >
                <i className="fa fa-plus fa-fw" aria-hidden="true" /> Add Item
              </Button>
            </div>
          </div>
          
          {/* Only render FieldArray when form values are initialized and not loading */}
          {(() => {
            const shouldRenderForm = values && event && !isLoadingItems
            if (!shouldRenderForm) {
              return (
                <div className="text-muted p-3">
                  {apiError ? (
                    <div className="text-danger">
                      <i className="fa fa-exclamation-triangle"></i> {apiError}
                    </div>
                  ) : isLoadingItems ? (
                    <div>
                      <i className="fa fa-spinner fa-spin"></i> Loading invoice form...
                    </div>
                  ) : (
                    <div className="text-warning">
                      <i className="fa fa-exclamation-triangle"></i> Unable to load form data. Please try refreshing the page.
                    </div>
                  )}
                </div>
              )
            }
            return values && !isLoadingItems && !apiError
          })() ? (
            <FieldArray name="items">
            {({ push, remove }) => {
              // Store the push function in useRef to enable the Add Items button at the top
              addItemRef.current = push
              
              return (
                <>
                  <DataTable
                  thead={[
                    'Type',
                    'Item',
                    'Quantity', 
                    'Unit Price',
                    'Total',
                    'Actions'
                  ]}
                  tbody={[
                    ...(values.items || []).map((item, index) => [
                      <div style={{ width: '150px' }}>
                        <select
                          name={`items.${index}.type`}
                          value={item.type}
                          onChange={e => {
                            setFieldValue(`items.${index}.type`, e.target.value)
                            setFieldValue(`items.${index}.id`, '')
                          }}
                          disabled={invoice && invoice.status !== 'draft'}
                          className="form-control"
                        >
                          <option value="">Select Type</option>
                          <option value="ticket">Ticket</option>
                          <option value="addon">Add-on</option>
                        </select>
                      </div>,
                      <div>
                        <select
                          name={`items.${index}.id`}
                          value={item.id}
                          onChange={e => {
                            const itemId = e.target.value
                            setFieldValue(`items.${index}.id`, itemId)
                            // Set the original price when item is selected
                            const originalPrice = getItemPrice(item.type, itemId)
                            setFieldValue(`items.${index}.original_price`, originalPrice)
                          }}
                          disabled={!item.type || (invoice && invoice.status !== 'draft')}
                          className="form-control"
                        >
                          <option value="">Select Item</option>
                          {(item.type === 'ticket' ? (availableItems.ticket_types || []) : (availableItems.addons || [])).map(i => (
                            <option key={i.id} value={i.id}>
                              {i.name} ({formatCurrency(i.price, event?.currency?.currency || 'USD')})
                              {i.available_quantity !== null && ` - ${i.available_quantity} available`}
                            </option>
                          ))}
                        </select>
                        {/* Hidden field for original price */}
                        <input
                          type="hidden"
                          name={`items.${index}.original_price`}
                          value={item.original_price || 0}
                        />
                      </div>,
                      <div style={{ width: '100px' }}>
                        <input
                          name={`items.${index}.quantity`}
                          type="number"
                          min="1"
                          value={item.quantity || ''}
                          onChange={e => {
                            const value = e.target.value
                            setFieldValue(`items.${index}.quantity`, value === '' ? '' : parseInt(value) || 1)
                          }}
                          onBlur={e => {
                            if (!e.target.value || parseInt(e.target.value) < 1) {
                              setFieldValue(`items.${index}.quantity`, 1)
                            }
                          }}
                          disabled={invoice && invoice.status !== 'draft'}
                          className="form-control"
                        />
                      </div>,
                      <div style={{ width: '150px' }}>
                        <input
                          name={`items.${index}.custom_price`}
                          type="number"
                          step="0.01"
                          min="0"
                          value={item.custom_price || ''}
                          placeholder={getItemPrice(item?.type, item?.id)?.toFixed(2) || '0.00'}
                          onChange={e => {
                            const value = e.target.value
                            setFieldValue(`items.${index}.custom_price`, value === '' ? null : parseFloat(value))
                          }}
                          disabled={invoice && invoice.status !== 'draft'}
                          className="form-control"
                        />
                      </div>,
                      <div style={{ color: '#ffffff', width: '120px' }}>
                        {formatCurrency(
                          (item?.custom_price || getItemPrice(item?.type, item?.id)) * (item?.quantity || 1),
                          event?.currency?.currency || 'USD'
                        )}
                      </div>,
                      <div style={{ width: '50px' }}>
                        {(!invoice || invoice?.status === 'draft') && (
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => remove(index)}
                            disabled={invoice && invoice.status !== 'draft'}
                          >
                            <i className="fa fa-trash" />
                          </Button>
                        )}
                      </div>
                    ])
                  ]}
                  tfoot={(() => {
                    const subtotal = calculateSubtotal(values.items)
                    const taxRate = parseFloat(values.tax_rate) || 0
                    const taxAmount = calculateTaxAmount(subtotal, taxRate)
                    const total = subtotal + taxAmount
                    const currency = event?.currency?.currency || 'USD'
                    
                    
                    const footerRows = [
                      // Subtotal row
                      ['', '', '', 
                        <strong style={{color: '#ffffff'}}>Subtotal:</strong>,
                        <strong style={{color: '#ffffff'}}>
                          {formatCurrency(subtotal, currency)}
                        </strong>,
                        ''
                      ],
                      // Tax row (always shown)
                      [
                        '', '', '', 
                        <strong style={{color: '#ffffff'}}>
                          {(() => {
                            const taxName = values.tax_name || 'Tax'
                            const taxRate = parseFloat(values.tax_rate) || 0
                            return taxRate > 0 ? `${taxName} ${taxRate}%:` : `${taxName}:`
                          })()}
                        </strong>,
                        <strong style={{color: '#ffffff'}}>
                          {formatCurrency(taxAmount, currency)}
                        </strong>,
                        ''
                      ]
                    ]
                    
                    // Total row
                    footerRows.push([
                      '', '', '', 
                      <strong style={{color: '#ffffff'}}>Total:</strong>,
                      <strong style={{color: '#ffffff'}}>
                        {formatCurrency(total, currency)}
                      </strong>,
                      ''
                    ])
                    
                    return footerRows
                  })()}
                  />
                </>
              )
            }}
          </FieldArray>
          ) : (
            <div className="text-muted p-3">
              {apiError ? (
                <div className="text-danger">
                  <i className="fa fa-exclamation-triangle"></i> {apiError}
                </div>
              ) : isLoadingItems ? (
                <div>
                  <i className="fa fa-spinner fa-spin"></i> Loading invoice form...
                </div>
              ) : (
                <div className="text-warning">
                  <i className="fa fa-exclamation-triangle"></i> Unable to load form data. Please try refreshing the page.
                </div>
              )}
            </div>
          )}

          {errors.items && typeof errors.items === 'string' && (
            <div className="text-danger mt-2">{errors.items}</div>
          )}

          <div className="mt-4">
            {invoice?.status === 'draft' ? (
              <>
                <Button
                  type="submit"
                  disabled={isSubmitting || isLoading}
                  className="btn btn-success btn-shadow"
                >
                  {isLoading ? 'Saving...' : 'Save Draft'}
                </Button>
                <Button
                  onClick={() => handleSaveAndSendInvoice(values, { setSubmitting, setErrors })}
                  disabled={isSubmitting || isLoading || isSending || values.items.length === 0}
                  className="btn btn-info btn-secondary"
                >
                  {isSending ? 'Sending...' : 'Save & Send Invoice'}
                </Button>
              </>
            ) : !invoice ? (
              <Button
                type="submit"
                variant="primary"
                disabled={isSubmitting || isLoading || !isFormValid(values, errors)}
                className="btn btn-success btn-shadow"
              >
                {isLoading ? 'Creating...' : 'Create Invoice'}
              </Button>
            ) : null}
            
            <Button variant="secondary" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </Form>
        )
      }}
    </Formik>
  )
}

const mapStateToProps = state => ({
  currentInvoice: state.bulkInvoices.currentInvoice
})

const mapDispatchToProps = dispatch => ({
  createInvoice: (eventId, data) => dispatch(CREATE_BULK_INVOICE(eventId, data)),
  updateInvoice: (eventId, invoiceId, data) => dispatch(UPDATE_BULK_INVOICE(eventId, invoiceId, data)),
  sendInvoice: (eventId, invoiceId) => dispatch(SEND_BULK_INVOICE(eventId, invoiceId)),
  fetchBulkInvoice: (eventId, invoiceId) => dispatch(FETCH_BULK_INVOICE(eventId, invoiceId))
})

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(BulkInvoiceForm))